import { z } from 'zod';
import { apiClient } from '../client';

/**
 * "Contacter Kevin" (2026-09-22, attachments 2026-09-23). A member's message
 * becomes a service case in the Swoft CRM; the server keeps a local trace
 * either way. The server answers 400 when an attachment breaks a limit, 503
 * when the partner client is not configured and 502 (French message) when the
 * CRM did not take the case.
 *
 * The member does not classify their own message: no `category` is sent, and
 * the server opens every case as `Inquiry`.
 */

export const SUPPORT_CATEGORIES = ['Inquiry', 'Technical', 'FeatureRequest', 'Billing'] as const;
export type SupportCategory = (typeof SUPPORT_CATEGORIES)[number];

export interface CreateSupportRequestInput {
  subject: string;
  message: string;
  /** The page the member is on, so the case says where it was written. */
  page?: string;
  /** Images and PDF, at most three of five megabytes each. */
  files?: File[];
}

export const SupportRequestCreatedSchema = z.object({
  requestId: z.string(),
  swoftCaseId: z.string(),
});
export type SupportRequestCreated = z.infer<typeof SupportRequestCreatedSchema>;

export const SupportAttachmentSchema = z.object({
  filename: z.string(),
  content_type: z.string(),
  size: z.number(),
  /** The stored URL, empty when the file was folded into the case inline. */
  url: z.string(),
});
export type SupportAttachment = z.infer<typeof SupportAttachmentSchema>;

export const MySupportRequestSchema = z.object({
  id: z.string(),
  subject: z.string(),
  message: z.string(),
  category: z.enum(SUPPORT_CATEGORIES),
  page: z.string(),
  swoft_case_id: z.string(),
  status: z.enum(['sent', 'failed']),
  error: z.string(),
  attachments: z.array(SupportAttachmentSchema).default([]),
  created_date: z.string(),
});
export type MySupportRequest = z.infer<typeof MySupportRequestSchema>;

export async function createSupportRequest(input: CreateSupportRequestInput): Promise<SupportRequestCreated> {
  const files = input.files ?? [];
  // No file: the plain JSON body, unchanged. With files: multipart, because
  // that is the only way the browser sends the bytes.
  const body: unknown = files.length
    ? buildMultipart(input, files)
    : { subject: input.subject, message: input.message, page: input.page };
  const raw = await apiClient.post<unknown>('/support/requests', body);
  return SupportRequestCreatedSchema.parse(raw);
}

function buildMultipart(input: CreateSupportRequestInput, files: File[]): FormData {
  const form = new FormData();
  form.append('subject', input.subject);
  form.append('message', input.message);
  if (input.page) form.append('page', input.page);
  for (const file of files) form.append('attachments', file, file.name);
  return form;
}

export async function listMySupportRequests(opts?: { signal?: AbortSignal }): Promise<MySupportRequest[]> {
  const raw = await apiClient.get<{ requests?: unknown }>('/support/requests/mine', opts);
  return z.array(MySupportRequestSchema).parse(raw?.requests ?? []);
}
